import type { CardTextProps } from '@Components/BigBrowse/GridCardMesh/Components/Texts/CardText';
import type {
  PlaneSize,
  Position2D,
} from '@Components/BigBrowse/Model/ModelTypes';
import { getTextBlockSize } from '@Utils/3d';
import { Assets } from '@Utils/AssetsStore';
import { scale } from '@Utils/window';
import { sum } from 'lodash';
import { Dimensions } from 'react-native';

import type {
  CtaView,
  HeroAndTextView,
  HeroAndTextViewStyles,
  ParagraphView,
} from '../../common/MultiSectionView.types';
import { getParagraphsText } from './HeroAndTextLayoutLandscape.textPart';

export const Constants = {
  FIGMA_PAGE_WIDTH: 375,
  FIGMA_PADDING_HORIZONTAL: 20,
  FIGMA_PADDING_VERTICAL: 30,
  FIGMA_PARAGRAPHS_TOP_MARGIN: 27,
  FIGMA_PARAGRAPHS_LINE_HEIGHT: 18,
  FIGMA_CTA_SIZE_WIDTH: 155,
  FIGMA_CTA_SIZE_HEIGHT: 45,
  FIGMA_CTA_MARGIN_TOP: 30,
};

export const getTextPartPortrait = async (
  section: HeroAndTextView
): Promise<HeroAndTextViewStyles['textPart']> => {
  const padding = scaleOnFigmaPageWidth(Constants.FIGMA_PADDING_HORIZONTAL);
  const maxWidth = Dimensions.get('window').width - 2 * padding;

  const { title, titleColumn } = await getTitle(section, maxWidth);
  const { paragraphsColumn, paragraphs } = await getParagraphs(
    section,
    maxWidth
  );

  const cta = getCtaView(section);

  const page = getPage({
    titleColumn: titleColumn?.size,
    paragraphs,
    cta,
    maxWidth,
  });

  return { title, titleColumn, paragraphs, paragraphsColumn, cta, page };
};

const scaleOnFigmaPageWidth = (width: number) =>
  scale(width, Dimensions.get('window').width, Constants.FIGMA_PAGE_WIDTH);

const getTitle = async (
  section: HeroAndTextView,
  maxWidth: number
): Promise<{
  titleColumn?: {
    size: PlaneSize;
    position: Position2D;
  };
  title?: {
    size: PlaneSize;
    props: CardTextProps;
  };
}> => {
  if (!section.dtoData.title) {
    return { title: undefined, titleColumn: undefined };
  }

  const titleProps: CardTextProps = {
    text: section.dtoData.title,
    fontAsset: Assets.Font_Gilroy_Bold,
    fontSize: 26,
    maxWidth,
    lineHeight: 32 / 26,
    letterSpacing: 0.03,
    color: section.dtoData.fontColour ?? 'black',
  };

  const textSize = await getTextBlockSize(titleProps);

  return {
    title: { size: textSize, props: titleProps },
    titleColumn: {
      size: { width: maxWidth, height: textSize.height },
      // we don't really care about position as it will be positioned
      // dynamically based on flex
      position: { x: 0, y: 0 },
    },
  };
};

const getParagraphs = async (
  section: HeroAndTextView,
  maxWidth: number
): Promise<{
  paragraphsColumn?: {
    size: PlaneSize;
    position: Position2D;
  };
  paragraphs?: ParagraphView[];
}> => {
  const paragraphs = getParagraphsText(section);

  if (paragraphs.length === 0) {
    return { paragraphsColumn: undefined, paragraphs: undefined };
  }

  const paragraphViews = await Promise.all(
    paragraphs.map((paragraph, index) =>
      getParagraphView({ paragraph, index, maxWidth, section })
    )
  );

  return {
    // we don't really care about size as they will be layed out by
    // flex
    paragraphsColumn: undefined,
    paragraphs: paragraphViews,
  };
};

type GetParagraphViewParams = {
  paragraph: string;
  index: number;
  maxWidth: number;
  section: HeroAndTextView;
};
const getParagraphView = async ({
  paragraph,
  index,
  maxWidth,
  section,
}: GetParagraphViewParams): Promise<ParagraphView> => {
  const fontAsset =
    index === 0 ? Assets.Font_Gilroy_Black : Assets.Font_Gilroy_Medium;

  const props: CardTextProps = {
    text: paragraph,
    fontAsset,
    fontSize: 14,
    maxWidth,
    lineHeight: Constants.FIGMA_PARAGRAPHS_LINE_HEIGHT / 14,
    letterSpacing: 0.03,
    color: section.dtoData.fontColour ?? 'black',
  };

  const size = await getTextBlockSize(props);

  return { size: { width: maxWidth, height: size.height }, props };
};

const getCtaView = (section: HeroAndTextView): CtaView | undefined => {
  if (!section.dtoData.linkButtonUrl || !section.dtoData.linkButtonTitle) {
    return undefined;
  }

  const props: CardTextProps = {
    text: section.dtoData.linkButtonTitle,
    fontSize: 14,
    fontAsset: Assets.Font_Gilroy_SemiBold,
    lineHeight: 18 / 14,
    letterSpacing: 0.03,
  };

  return {
    size: {
      width: Constants.FIGMA_CTA_SIZE_WIDTH,
      height: Constants.FIGMA_CTA_SIZE_HEIGHT,
    },
    marginTop: Constants.FIGMA_CTA_MARGIN_TOP,
    props,
    position: 'moreCta_under_paragraphs',
  };
};

const getPage = ({
  titleColumn,
  paragraphs = [],
  cta,
  maxWidth,
}: {
  titleColumn?: PlaneSize;
  paragraphs?: ParagraphView[];
  cta?: CtaView;
  maxWidth: number;
}): { size: PlaneSize } => {
  const hasTitle = Boolean(titleColumn);
  const hasParagraphs = Boolean(paragraphs?.length > 0);
  const hasCta = Boolean(cta);

  if (!hasTitle && !hasParagraphs && !hasCta) {
    return { size: { width: 0, height: 0 } };
  }

  const titleHeight = titleColumn?.height ?? 0;

  const paragraphsTopMargin =
    hasTitle && hasParagraphs ? Constants.FIGMA_PARAGRAPHS_TOP_MARGIN : 0;
  const paragraphsHeight = sum(paragraphs.map((p) => p.size.height));
  const paragraphsPaddings =
    Math.max(0, paragraphs.length - 1) * Constants.FIGMA_PARAGRAPHS_LINE_HEIGHT;
  const paragraphsTotal =
    paragraphsHeight + paragraphsTopMargin + paragraphsPaddings;

  const ctaMargin =
    hasCta && (hasTitle || hasParagraphs) ? Constants.FIGMA_CTA_MARGIN_TOP : 0;
  const ctaHeight = hasCta ? cta!.size.height : 0;
  const ctaTotal = ctaMargin + ctaHeight;

  const height =
    Constants.FIGMA_PADDING_VERTICAL +
    titleHeight +
    paragraphsTotal +
    ctaTotal +
    Constants.FIGMA_PADDING_VERTICAL;

  return { size: { width: maxWidth, height } };
};
